import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Mail, Download, Trash2, Filter, X, RefreshCw } from "lucide-react";

export default function AdminNewsletter() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter && filter !== "all" ? { status: filter } : {};
      const r = await api.get("/admin/newsletter", { params });
      setItems(r.data.items || []);
      setStats({
        total: r.data.total || 0,
        active: r.data.active || 0,
        unsubscribed: r.data.unsubscribed || 0,
      });
    } catch (e) {
      window.alert(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/newsletter/export", {
        params: { status: filter || "active" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `newsletter_${filter || "active"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sterge acest abonat?")) return;
    try {
      await api.delete(`/admin/newsletter/${id}`);
      load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const fmt = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-newsletter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif text-3xl">Newsletter</h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="newsletter-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Reincarca
          </button>
          <button onClick={exportCsv} className="btn-gold-filled inline-flex items-center gap-2 text-sm" data-testid="newsletter-export">
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { key: "all", label: "Total", value: stats.total },
          { key: "active", label: "Activi", value: stats.active },
          { key: "unsubscribed", label: "Dezabonati", value: stats.unsubscribed },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`card-surface p-4 text-left transition ${filter === s.key ? "ring-1 ring-[var(--brand-primary)]" : ""}`}
            data-testid={`newsletter-filter-${s.key}`}
          >
            <div className="text-2xl font-serif" style={{ color: "var(--brand-primary)" }}>{s.value}</div>
            <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>{s.label}</div>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm" style={{ color: "var(--brand-text-secondary)" }} data-testid="newsletter-empty">
          Niciun abonat {filter !== "all" ? `cu statusul "${filter}"` : "inca"}.
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(255,255,255,0.02)" }}>
              <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--brand-text-secondary)" }}>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Nume</th>
                <th className="px-4 py-3">Lang</th>
                <th className="px-4 py-3">Sursa</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`newsletter-row-${it.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <a href={`mailto:${it.email}`} className="hover:text-[var(--brand-primary)] inline-flex items-center gap-1">
                      <Mail className="w-3 h-3" />{it.email}
                    </a>
                  </td>
                  <td className="px-4 py-3">{it.name || "-"}</td>
                  <td className="px-4 py-3 uppercase text-xs">{it.lang || "-"}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--brand-text-secondary)" }}>{it.source || "-"}</td>
                  <td className="px-4 py-3">
                    <span
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{
                        color: it.status === "active" ? "#22c55e" : "#94a3b8",
                        background: it.status === "active" ? "rgba(34,197,94,0.15)" : "rgba(148,163,184,0.15)",
                      }}
                    >
                      {it.status === "active" ? "Activ" : "Dezabonat"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--brand-text-secondary)" }}>{fmt(it.subscribed_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(it.id)} className="p-1 hover:bg-white/5 rounded" title="Sterge" data-testid={`newsletter-delete-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--brand-text-secondary)" }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
